import type { DechaineEffect } from '../../../data/full/avatar';
import { unitFromAngle } from '../../../shared/angle';
import { clamp } from '../../../shared/scalar';
import type { Tempo } from '../../../shared/tempo';
import { distanceSquared, normalize } from '../../../shared/vec';
import { hurtEnemy, keepWhere } from '../../effects';
import { applyEnemyStatus } from '../../enemy-status';
import { moveUntilBlocked } from '../../obstacles';
import type { SkillModule } from '../../skills/types';
import type { EnemyState, PlayerState, PropState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import {
  AIR,
  ELEMENTS,
  FIRE,
  WATER,
  elementOfTick,
  isSmall,
  keepInside,
  strike,
} from '../attacks/cycle';
import { ownerOf } from '../../props';

export const UNLEASHED = 'unleashed';
export const SWEPT = 'swept';

const LINGER_BEATS = 2;

export function unleashedTicks(effect: DechaineEffect, element: number, tempo: Tempo): number {
  switch (element) {
    case FIRE:
      return (effect.ring.beats + LINGER_BEATS) * tempo.ticksPerBeat;
    case WATER:
      return effect.wave.beats * tempo.ticksPerBeat;
    case AIR:
      return effect.tornado.bars * tempo.ticksPerBar;
    default:
      return (effect.rift.beats + LINGER_BEATS) * tempo.ticksPerBeat;
  }
}

export function unleashedReach(
  effect: DechaineEffect,
  element: number,
  age: number,
  size: number,
  tempo: Tempo,
): number {
  const grown = (beats: number) => Math.min(1, age / (beats * tempo.ticksPerBeat));
  switch (element) {
    case FIRE:
      return effect.ring.radius * size * grown(effect.ring.beats);
    case WATER:
      return effect.wave.reach * grown(effect.wave.beats);
    case AIR:
      return effect.tornado.radius * size;
    default:
      return effect.rift.length * size * grown(effect.rift.beats);
  }
}

// Unleashed by Chaîne des éléments, not the element of the bar Déchaîné was cast in, read after a step.
export function isChained(
  loose: PropState,
  effect: DechaineEffect,
  tick: number,
  tempo: Tempo,
): boolean {
  const element = loose.rank ?? FIRE;
  const age = unleashedTicks(effect, element, tempo) - loose.ticksLeft;
  return elementOfTick(tick - age + 1, tempo) !== element;
}

function dechaineOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): DechaineEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'dechaine') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" unleashes nothing`);
  }
  return effect;
}

export const dechaine: SkillModule<DechaineEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state, tempo } = ctx;
    const first = elementOfTick(state.tick, tempo);
    const more = clamp(Math.floor(statValue(player, 'skillCountAdd', 0)), 0, ELEMENTS - 1);
    const way = normalize(player.aim);
    const ahead = { x: player.x + way.x * effect.aim, y: player.y + way.y * effect.aim };
    keepInside(state, ahead);
    for (let index = 0; index <= more; index++) {
      const element = (first + index) % ELEMENTS;
      const from = element === FIRE || element === AIR ? ahead : player;
      const loose: PropState = {
        id: state.nextEntityId,
        kind: UNLEASHED,
        playerId: player.id,
        phase: 'flying',
        x: from.x,
        y: from.y,
        prevX: from.x,
        prevY: from.y,
        vx: way.x,
        vy: way.y,
        damage: effect.damage * power,
        ticksLeft: unleashedTicks(effect, element, tempo),
        hitsLeft: 0,
        hitIds: [],
        rank: element,
      };
      state.nextEntityId += 1;
      (state.props ??= []).push(loose);
    }
  },

  update(ctx) {
    const props = ctx.state.props;
    if (props === undefined) {
      return;
    }
    let gone = false;
    for (const prop of props) {
      if (prop.kind === UNLEASHED) {
        rage(ctx, prop);
        gone ||= prop.ticksLeft <= 0;
      } else if (prop.kind === SWEPT) {
        carry(ctx, prop);
        gone ||= prop.ticksLeft <= 0;
      }
    }
    if (gone) {
      keepWhere(
        props,
        (prop) => (prop.kind !== UNLEASHED && prop.kind !== SWEPT) || prop.ticksLeft > 0,
      );
      if (props.length === 0) {
        delete ctx.state.props;
      }
    }
  },
};

function rage(ctx: StepContext, loose: PropState): void {
  const { state, tempo } = ctx;
  const owner = ownerOf(state, loose);
  const effect = dechaineOf(ctx, owner);
  const element = loose.rank ?? FIRE;
  const size = statValue(owner, 'skillRadiusMul', 1);
  const age = unleashedTicks(effect, element, tempo) - loose.ticksLeft + 1;
  const reach = unleashedReach(effect, element, age, size, tempo);
  loose.ticksLeft -= 1;
  if (element === AIR) {
    whirl(ctx, owner, loose, effect, size, reach);
    return;
  }
  const hitIds = (loose.hitIds ??= []);
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0 || hitIds.includes(enemy.id)) {
      continue;
    }
    const at = meets(loose, element, enemy, reach, effect, size);
    if (at === undefined) {
      continue;
    }
    hitIds.push(enemy.id);
    strike(ctx, owner, element, enemy, loose.damage, at);
    if (element === WATER && isSmall(ctx, enemy) && enemy.hp > 0) {
      sweep(ctx, loose, enemy, effect);
    }
  }
}

function meets(
  loose: PropState,
  element: number,
  enemy: EnemyState,
  reach: number,
  effect: DechaineEffect,
  size: number,
): Vec2 | undefined {
  if (element === FIRE) {
    const away = reach + enemy.radius;
    return distanceSquared(enemy, loose) <= away * away ? loose : undefined;
  }
  const dx = enemy.x - loose.x;
  const dy = enemy.y - loose.y;
  const along = dx * loose.vx + dy * loose.vy;
  const across = Math.abs(dx * loose.vy - dy * loose.vx);
  const half = element === WATER ? (effect.wave.width * size) / 2 : effect.rift.width;
  if (along < -enemy.radius || along > reach + enemy.radius || across > half + enemy.radius) {
    return undefined;
  }
  const behind = Math.max(0, along - enemy.radius);
  return { x: loose.x + loose.vx * behind, y: loose.y + loose.vy * behind };
}

function whirl(
  ctx: StepContext,
  owner: PlayerState,
  tornado: PropState,
  effect: DechaineEffect,
  size: number,
  reach: number,
): void {
  const { state, tempo } = ctx;
  const { speed, wander, pull, suck } = effect.tornado;
  const turn = unitFromAngle(speed / wander);
  const heading = normalize({
    x: tornado.vx * turn.x - tornado.vy * turn.y,
    y: tornado.vx * turn.y + tornado.vy * turn.x,
  });
  tornado.vx = heading.x;
  tornado.vy = heading.y;
  tornado.x += heading.x * speed;
  tornado.y += heading.y * speed;
  keepInside(state, tornado);
  const hitIds = (tornado.hitIds ??= []);
  const sucks = pull * size;
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0) {
      continue;
    }
    const distance = Math.sqrt(distanceSquared(enemy, tornado));
    if (distance <= reach + enemy.radius) {
      if (!hitIds.includes(enemy.id)) {
        hitIds.push(enemy.id);
        hurtEnemy(state, enemy, tornado.damage, owner.id, tornado);
      }
      if (enemy.isBoss) {
        applyEnemyStatus(state, enemy, 'slowed', tempo.ticksPerBeat);
      }
    }
    if (distance > 0 && distance <= sucks + enemy.radius && isSmall(ctx, enemy)) {
      const step = Math.min(suck, distance) / distance;
      const dx = (tornado.x - enemy.x) * step;
      const dy = (tornado.y - enemy.y) * step;
      moveUntilBlocked(enemy, dx, dy, ctx.enemyObstacles);
    }
  }
}

function sweep(ctx: StepContext, wave: PropState, enemy: EnemyState, effect: DechaineEffect): void {
  const { state, tempo } = ctx;
  const ticks = tempo.ticksPerBeat;
  (state.props ??= []).push({
    id: state.nextEntityId,
    kind: SWEPT,
    playerId: wave.playerId,
    phase: 'flying',
    x: enemy.x,
    y: enemy.y,
    prevX: enemy.x,
    prevY: enemy.y,
    vx: (wave.vx * effect.wave.carry) / ticks,
    vy: (wave.vy * effect.wave.carry) / ticks,
    damage: 0,
    ticksLeft: ticks,
    hitsLeft: 0,
    targetId: enemy.id,
  });
  state.nextEntityId += 1;
}

function carry({ state, enemyObstacles }: StepContext, swept: PropState): void {
  const enemy = state.enemies.find((candidate) => candidate.id === swept.targetId);
  swept.ticksLeft -= 1;
  if (enemy === undefined || enemy.hp <= 0 || enemy.isBoss) {
    swept.ticksLeft = 0;
    return;
  }
  moveUntilBlocked(enemy, swept.vx, swept.vy, enemyObstacles);
  keepInside(state, enemy);
  swept.x = enemy.x;
  swept.y = enemy.y;
}
